import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatEuros } from "@sundial/core";
import { useMonth, useSettings, usePayments, useUrssaf, useReportRange, useClients } from "../api/queries.js";
import { SunTile } from "../components/SunTile.js";
import { MonthPicker } from "../components/MonthPicker.js";
import type { DayStatus } from "@sundial/core";
import { isOffDay } from "../lib/offday.js";
import { DayEditor } from "../components/DayEditor.js";
import { RevenueCard } from "../components/RevenueCard.js";
import { CeilingCard } from "../components/CeilingCard.js";
import { UpcomingCard } from "../components/UpcomingCard.js";
import { ClientFilter } from "../components/ClientFilter.js";
import { exportRelevePdf } from "../lib/exportRelevePdf.js";

/* ── Helpers ──────────────────────────────────────────── */

function todayParis(): string {
  return new Intl.DateTimeFormat("fr-CA", {
    timeZone: "Europe/Paris",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function thisMonth(): string {
  return todayParis().slice(0, 7); // YYYY-MM
}

function parseYM(m: string): [number, number] {
  const parts = m.split("-");
  return [parseInt(parts[0] ?? "2026", 10), parseInt(parts[1] ?? "1", 10)];
}

function prevMonth(m: string): string {
  const [y, mo] = parseYM(m);
  const d = new Date(y, mo - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function nextMonth(m: string): string {
  const [y, mo] = parseYM(m);
  const d = new Date(y, mo, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(m: string): string {
  const [y, mo] = parseYM(m);
  return new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" }).format(
    new Date(y, mo - 1, 1),
  );
}

/** Returns an array of date strings for all days in the month, padded to start on Monday */
function buildCalendarGrid(month: string): (string | null)[] {
  const [y, mo] = parseYM(month);
  const firstDay = new Date(y, mo - 1, 1);
  const daysInMonth = new Date(y, mo, 0).getDate();

  // Monday=0 ... Sunday=6
  const startOffset = (firstDay.getDay() + 6) % 7;

  const cells: (string | null)[] = Array(startOffset).fill(null);
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push(`${month}-${String(d).padStart(2, "0")}`);
  }
  // Pad to a complete week
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/* ── Month picker popover (year nav + 12-month grid) ──── */

/* ── Payment badge helper ─────────────────────────────── */
function paymentBadge(status: string): { label: string; color: string; bg: string } {
  switch (status) {
    case "received":
      return {
        label: "Received",
        color: "var(--success, #22c55e)",
        bg: "rgba(34,197,94,.12)",
      };
    case "pending":
      return {
        label: "Pending",
        color: "var(--warning, #f59e0b)",
        bg: "rgba(245,158,11,.12)",
      };
    default:
      return {
        label: "Not landed",
        color: "var(--danger, #ef4444)",
        bg: "rgba(239,68,68,.12)",
      };
  }
}

/* ── URSSAF Card ──────────────────────────────────────── */
export function UrssafCard() {
  const { t } = useTranslation();
  const { data } = useUrssaf();

  if (!data) return null;

  const barColor =
    data.ceilingPct >= 100
      ? "var(--danger, #ef4444)"
      : data.ceilingPct >= 80
        ? "var(--warning, #f59e0b)"
        : "var(--primary)";

  const barPct = Math.min(data.ceilingPct, 100);

  return (
    <div
      className="card"
      data-testid="urssaf-card"
      style={{ marginTop: "14px", padding: "18px 20px" }}
    >
      {/* Title row */}
      <div
        style={{
          font: "600 11px/14px var(--font-head)",
          letterSpacing: ".07em",
          textTransform: "uppercase",
          color: "var(--muted2)",
          marginBottom: "12px",
        }}
      >
        {t("urssaf.title")}
      </div>

      {/* Set-aside line */}
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "8px",
          marginBottom: "6px",
        }}
      >
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontVariantNumeric: "tabular-nums",
            fontWeight: 600,
            fontSize: "24px",
            color: "var(--helio-orange)",
          }}
        >
          {formatEuros(data.setAsideCents)}
        </span>
        <span style={{ fontSize: "12.5px", color: "var(--muted)" }}>
          {t("urssaf.setAside")} ~{data.ratePct}%
        </span>
      </div>

      {/* CA / ceiling line */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontSize: "13px",
          color: "var(--muted)",
          marginBottom: "8px",
        }}
      >
        <span>
          {t("urssaf.ca")} {formatEuros(data.receivedYtdCents)}
        </span>
        <span style={{ fontWeight: 500, color: "var(--text)" }}>
          / {formatEuros(data.ceilingCents)} {t("urssaf.ceiling")}
        </span>
      </div>

      {/* Progress bar */}
      <div
        style={{
          height: "8px",
          borderRadius: "999px",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          overflow: "hidden",
          marginBottom: "10px",
        }}
        role="progressbar"
        aria-valuenow={data.ceilingPct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${t("urssaf.ceiling")} ${data.ceilingPct.toFixed(1)}%`}
      >
        <div
          style={{
            height: "100%",
            width: `${barPct}%`,
            background: barColor,
            borderRadius: "999px",
            transition: "width .3s",
          }}
        />
      </div>
      <div
        style={{
          fontSize: "12px",
          color: barColor,
          fontWeight: 500,
          marginBottom: "10px",
        }}
      >
        {data.ceilingPct >= 100
          ? `⚠ Plafond dépassé (${data.ceilingPct.toFixed(1)}%)`
          : data.ceilingPct >= 80
            ? `Attention : ${data.ceilingPct.toFixed(1)}% du plafond atteint`
            : `${data.ceilingPct.toFixed(1)}% du plafond`}
      </div>

      {/* VAT line */}
      <div
        style={{
          fontSize: "12.5px",
          color: data.vatApplicable ? "var(--danger, #ef4444)" : "var(--muted)",
          padding: "8px 12px",
          borderRadius: "8px",
          background: data.vatApplicable
            ? "rgba(239,68,68,.08)"
            : "var(--surface)",
          border: "1px solid var(--border)",
        }}
      >
        {data.vatApplicable ? t("urssaf.vatApplicable") : t("urssaf.vatFranchise")}
      </div>
    </div>
  );
}

/* ── Dashboard ────────────────────────────────────────── */
export function Dashboard() {
  const { t } = useTranslation();
  const [month, setMonth] = useState<string>(thisMonth);
  const [editingDate, setEditingDate] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [clientId, setClientId] = useState("");

  const { data: monthData, isLoading: loadingMonth } = useMonth(month);
  const { data: settings } = useSettings();
  const { data: paymentsData } = usePayments();
  const { data: clientsData } = useClients();
  const clientName = clientId ? (clientsData?.clients.find((c) => c.id === clientId)?.name ?? "") : "";

  /* The relevé's money breakdown (HT/TVA/TTC, franchise crossing, received,
     owed) is computed server-side. Fetch the same range endpoint Reports uses,
     scoped to the shown month, so the Home PDF matches the accountant statement
     exactly rather than recomputing it here. */
  const monthStart = `${month}-01`;
  const [yEnd, moEnd] = parseYM(month);
  const monthEnd = `${month}-${String(new Date(yEnd, moEnd, 0).getDate()).padStart(2, "0")}`;
  // Scoped to the active client filter: totals stay person-level (for the relevé
  // export), and clientActivity carries the client's own activity for the KPIs.
  const { data: rangeData } = useReportRange(monthStart, monthEnd, "both", clientId || undefined);
  const urssafEnabled = settings?.urssafEnabled ?? false;
  const frHolidaysEnabled = settings?.frHolidaysEnabled ?? true;

  // Build a quick lookup for day statuses and notes
  const dayMap = new Map<string, DayStatus>();
  const noteMap = new Map<string, string>();
  const clientMap = new Map<string, string>();
  const allocMap = new Map<string, { clientId: string; status: "full" | "half" }[]>();
  if (monthData) {
    for (const d of monthData.days) {
      dayMap.set(d.date, d.status);
      if (d.note) noteMap.set(d.date, d.note);
      if (d.clientId) clientMap.set(d.date, d.clientId);
      if (d.allocations?.length) allocMap.set(d.date, d.allocations);
    }
  }

  // A day matches the client filter if that client is the day's primary OR one
  // of its extra allocations (multi-client days stay lit). Same rule as the
  // Calendar screen, so the two heatmaps behave identically.
  const matchesClient = (date: string): boolean => {
    if (!clientId) return true;
    return clientMap.get(date) === clientId || (allocMap.get(date) ?? []).some((a) => a.clientId === clientId);
  };
  const anyClientMatch =
    !clientId || [...dayMap.keys()].some((d) => d.startsWith(month) && matchesClient(d));

  const summary = monthData?.summary ?? {
    full: 0,
    half: 0,
    none: 0,
    sick: 0,
    holiday: 0,
    billable: 0,
  };

  const tjmCents = settings?.tjmCents ?? 0;
  const expectedCentsVal = Math.round(summary.billable * tjmCents);
  const totalOwedCents = paymentsData?.totalOwedCents ?? 0;

  // Hero card: "Am I owed money?"
  const settled = totalOwedCents <= 0;
  const hero = settled
    ? {
        bg: "linear-gradient(135deg, rgba(34,197,94,.08), rgba(34,197,94,.02))",
        border: "rgba(34,197,94,.25)",
        glow: "rgba(34,197,94,.18)",
        color: "var(--success, #22c55e)",
        label2: "var(--success, #22c55e)",
        amount: "All clear",
        sub: "No outstanding invoices",
      }
    : {
        bg: "linear-gradient(135deg, rgba(239,68,68,.09), rgba(239,68,68,.02))",
        border: "rgba(239,68,68,.25)",
        glow: "rgba(239,68,68,.18)",
        color: "var(--danger, #ef4444)",
        label2: "var(--danger, #ef4444)",
        amount: formatEuros(totalOwedCents),
        sub: "Outstanding across all invoices",
      };

  // Payment badge from latest payment
  const payments = paymentsData?.payments ?? [];
  const latestPayment = payments[0];
  const badge = latestPayment
    ? paymentBadge(latestPayment.status)
    : { label: "No data", color: "var(--muted)", bg: "var(--surface)" };

  const grid = buildCalendarGrid(month);
  const todayStr = todayParis();

  const isCurrentMonth = month === thisMonth();
  const workedDays = summary.full + summary.half;

  // Client-scoped KPIs — when a filter is active the tiles show that client's
  // activity, not the whole month's. Person-level bits (URSSAF, deadlines, the
  // owed hero) stay global. Worked-day COUNT is derived from the matching days;
  // billable weight and expected HT come from the scoped clientActivity.
  const clientActivity = clientId ? (rangeData?.clientActivity ?? null) : null;
  let clientWorkedCount = 0;
  if (clientId) {
    for (const [d, st] of dayMap) {
      if ((st === "full" || st === "half") && matchesClient(d)) clientWorkedCount++;
    }
  }
  const workedDisplay = clientId ? clientWorkedCount : workedDays;
  // Unfiltered billable/expected come from the range totals, which FOLD extra
  // client allocations (a day billed to two clients counts for both). Using the
  // day_entries-only summary here would undercount multi-client days and read
  // less than the sum of the per-client figures.
  const billableDisplay = clientActivity ? clientActivity.billableDays : (rangeData?.totals.billableDays ?? summary.billable);
  const expectedDisplayCents = clientActivity ? clientActivity.expectedHtCents : (rangeData?.totals.expectedHtCents ?? expectedCentsVal);

  return (
    <div className="page-content">
      {/* ── Header: centered month switcher (with month picker) + export ── */}
      <div style={{ position: "relative", marginBottom: "16px", minHeight: "56px" }}>
        {/* Centered switcher */}
        <div
          style={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {/* Month label — click to open the month picker */}
          <button
            onClick={() => setPickerOpen((v) => !v)}
            aria-haspopup="dialog"
            aria-expanded={pickerOpen}
            style={{
              font: "600 11px/14px var(--font-head)",
              letterSpacing: ".07em",
              textTransform: "uppercase",
              color: "var(--muted2)",
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            {monthLabel(month)}
            <span aria-hidden="true" style={{ fontSize: "9px" }}>▾</span>
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <button
              className="btn secondary"
              style={{ height: "32px", padding: "0 10px", fontSize: "16px" }}
              onClick={() => setMonth(prevMonth)}
              aria-label="Previous month"
            >
              ‹
            </button>
            <button
              className="btn secondary"
              style={{ height: "32px", padding: "0 10px", fontSize: "13px" }}
              onClick={() => setMonth(thisMonth)}
              disabled={isCurrentMonth}
            >
              This month
            </button>
            <button
              className="btn secondary"
              style={{ height: "32px", padding: "0 10px", fontSize: "16px" }}
              onClick={() => setMonth(nextMonth)}
              aria-label="Next month"
            >
              ›
            </button>
          </div>
          {pickerOpen && (
            <MonthPicker value={month} onPick={setMonth} onClose={() => setPickerOpen(false)} />
          )}
        </div>

        {/* Export — the branded relevé d'activité for the month, downloaded
            directly. Same document Reports produces, so Home and Reports never
            disagree on the numbers. */}
        <button
          className="btn primary"
          onClick={() => {
            const t = rangeData?.totals;
            if (!t) return;
            exportRelevePdf({
              identity: {
                name: settings?.bizName ?? "",
                siret: settings?.bizSiret ?? "",
                address: settings?.bizAddress ?? "",
              },
              monthLabel: monthLabel(month),
              generatedOn: new Intl.DateTimeFormat("fr-FR").format(new Date()),
              billableDays: t.billableDays,
              htCents: t.expectedHtCents,
              vatCents: t.vatCents,
              ttcCents: t.expectedCents,
              receivedCents: t.receivedCents,
              owedCents: t.owedCents,
              franchise: t.vatCents === 0,
              ...(rangeData?.clientBreakdown && rangeData.clientBreakdown.length > 1
                ? { breakdown: rangeData.clientBreakdown.map((b) => ({ name: b.name, days: b.days, htCents: b.htCents })) }
                : {}),
              days: (rangeData?.rows ?? []).map((r) => ({
                date: r.date,
                status: r.status.toLowerCase() as DayStatus,
                billable: r.billable,
              })),
              invoices: (rangeData?.payments ?? [])
                .filter((p) => p.invoiceRef)
                .map((p) => ({ period: p.period, ref: p.invoiceRef as string, url: p.invoiceUrl })),
            });
          }}
          disabled={loadingMonth || !rangeData?.totals}
          style={{ position: "absolute", right: 0, top: 0, height: "40px", gap: "7px" }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 3v12M8 11l4 4 4-4M5 21h14" />
          </svg>
          Export PDF
        </button>
      </div>

      {/* ── HERO: Am I owed money? ────────────────────────── */}
      <div
        style={{
          borderRadius: "16px",
          padding: "20px 22px",
          marginBottom: "14px",
          background: hero.bg,
          border: `1px solid ${hero.border}`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            right: "-26px",
            top: "-26px",
            width: "120px",
            height: "120px",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${hero.glow}, transparent 70%)`,
          }}
        />
        <div
          style={{
            font: "600 11px/14px var(--font-head)",
            letterSpacing: ".07em",
            textTransform: "uppercase",
            color: hero.label2,
            position: "relative",
          }}
        >
          Am I owed money?
        </div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontVariantNumeric: "tabular-nums",
            fontWeight: 600,
            fontSize: "38px",
            lineHeight: 1.1,
            letterSpacing: "-.01em",
            color: hero.color,
            marginTop: "6px",
            position: "relative",
          }}
        >
          {hero.amount}
        </div>
        <div
          style={{
            fontSize: "13.5px",
            color: "var(--muted)",
            marginTop: "4px",
            position: "relative",
          }}
        >
          {hero.sub}
        </div>
      </div>

      {/* ── Client filter (page scope) — everything below reacts: KPIs,
          calendar, and the Revenue card. Hidden at a single client. ── */}
      {(clientsData?.clients ?? []).filter((c) => !c.archived).length > 1 && (
        <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 14, flexWrap: "wrap" }}>
          <span style={{ font: "600 10px Sora", letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted2)", whiteSpace: "nowrap" }}>
            {t("clientFilter.label")}
          </span>
          <ClientFilter value={clientId} onChange={setClientId} />
        </div>
      )}

      {/* ── KPI grid ─────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "12px",
          marginBottom: "14px",
        }}
      >
        {/* Days worked */}
        <div className="card">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <span
              style={{
                font: "600 11px/14px var(--font-head)",
                letterSpacing: ".07em",
                textTransform: "uppercase",
                color: "var(--muted2)",
              }}
            >
              Days worked
            </span>
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--helio-orange)"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="4" />
              <path d="M12 4v1M12 19v1M5 5l1 1M18 18l1 1M4 12h1M19 12h1M5 19l1-1M18 6l1-1" />
            </svg>
          </div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontVariantNumeric: "tabular-nums",
              fontWeight: 600,
              fontSize: "30px",
              marginTop: "8px",
            }}
          >
            {loadingMonth ? "…" : workedDisplay}
          </div>
          <div style={{ fontSize: "12.5px", color: "var(--muted)", marginTop: "2px" }}>
            full + half days
          </div>
        </div>

        {/* Billable days */}
        <div className="card">
          <div
            style={{
              font: "600 11px/14px var(--font-head)",
              letterSpacing: ".07em",
              textTransform: "uppercase",
              color: "var(--muted2)",
            }}
          >
            Billable days
          </div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontVariantNumeric: "tabular-nums",
              fontWeight: 600,
              fontSize: "30px",
              marginTop: "8px",
            }}
          >
            {loadingMonth ? "…" : billableDisplay.toFixed(1)}
          </div>
          <div style={{ fontSize: "12.5px", color: "var(--muted)", marginTop: "2px" }}>
            full ×1 · half ×0.5
          </div>
        </div>

        {/* Expected € */}
        <div className="card">
          <div
            style={{
              font: "600 11px/14px var(--font-head)",
              letterSpacing: ".07em",
              textTransform: "uppercase",
              color: "var(--muted2)",
            }}
          >
            Expected
          </div>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontVariantNumeric: "tabular-nums",
              fontWeight: 600,
              fontSize: "30px",
              marginTop: "8px",
              color: "var(--helio-orange)",
            }}
          >
            {settings ? formatEuros(expectedDisplayCents) : "…"}
          </div>
          <div style={{ fontSize: "12.5px", color: "var(--muted)", marginTop: "2px" }}>
            TJM {settings ? formatEuros(tjmCents) : "…"}/day
          </div>
        </div>

        {/* Payment status */}
        <div className="card">
          <div
            style={{
              font: "600 11px/14px var(--font-head)",
              letterSpacing: ".07em",
              textTransform: "uppercase",
              color: "var(--muted2)",
            }}
          >
            Payment
          </div>
          <div style={{ marginTop: "12px" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "7px",
                padding: "6px 11px",
                borderRadius: "999px",
                background: badge.bg,
                color: badge.color,
                font: "600 13px var(--font-head)",
              }}
            >
              <span
                style={{
                  width: "7px",
                  height: "7px",
                  borderRadius: "50%",
                  background: badge.color,
                }}
              />
              {badge.label}
            </span>
          </div>
          <div style={{ fontSize: "12.5px", color: "var(--muted)", marginTop: "8px" }}>
            {latestPayment?.period ?? "Latest invoice"}
          </div>
        </div>
      </div>

      {/* ── Breakdown chips ──────────────────────────────── */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          marginBottom: "18px",
        }}
      >
        {[
          {
            key: "full",
            count: summary.full,
            icon: "☀️",
            label: "full",
            dotStyle: {
              background:
                "radial-gradient(circle at 50% 38%, var(--helio-orange-bright, #ff9a3c), var(--helio-orange) 72%)",
            },
          },
          {
            key: "half",
            count: summary.half,
            icon: "🌤️",
            label: "half",
            dotStyle: {
              background:
                "linear-gradient(to top, var(--helio-orange), transparent 75%), var(--tile-none)",
            },
          },
          {
            key: "none",
            count: summary.none,
            icon: "🌙",
            label: "none",
            dotStyle: { background: "var(--tile-none)" },
          },
          {
            key: "sick",
            count: summary.sick,
            icon: "🤒",
            label: "sick",
            dotStyle: { background: "var(--sick, #f87171)" },
          },
          {
            key: "holiday",
            count: summary.holiday,
            icon: "🏖️",
            label: "holiday",
            dotStyle: { background: "var(--holiday, #60a5fa)" },
          },
        ].map(({ key, count, icon, label, dotStyle }) => (
          <div
            key={key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "7px",
              padding: "7px 12px",
              borderRadius: "999px",
              background: "var(--card)",
              border: "1px solid var(--border)",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            <span
              style={{
                width: "11px",
                height: "11px",
                borderRadius: "3px",
                flexShrink: 0,
                ...dotStyle,
              }}
            />
            <span aria-hidden="true">{icon}</span>
            <span>
              {count} {label}
            </span>
          </div>
        ))}
      </div>

      {/* ── Heatmap — reacts to the client filter (non-matching days dim) ── */}
      <div className="card" style={{ padding: "18px 18px 16px" }}>
        <div
          style={{
            font: "600 11px/14px var(--font-head)",
            letterSpacing: ".07em",
            textTransform: "uppercase",
            color: "var(--muted2)",
            marginBottom: "12px",
          }}
        >
          {monthLabel(month)} — sun map
        </div>

        <div style={{ position: "relative" }}>
          <div
            role="grid"
            aria-label="Sun heatmap calendar"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "6px",
            }}
          >
            {/* Day-of-week headers */}
            {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
              <div
                key={i}
                style={{
                  textAlign: "center",
                  font: "600 10px var(--font-head)",
                  letterSpacing: ".04em",
                  color: "var(--muted2)",
                }}
              >
                {d}
              </div>
            ))}

            {/* Day tiles */}
            {grid.map((date, i) => {
              if (!date) {
                return <div key={`empty-${i}`} />;
              }
              const loggedStatus = dayMap.get(date);
              const status = loggedStatus ?? (isOffDay(date, frHolidaysEnabled) ? "none" : undefined);
              const isToday = date === todayStr;
              const billable = loggedStatus === "full" || loggedStatus === "half";
              const clients = billable ? 1 + (allocMap.get(date)?.length ?? 0) : 0;
              return (
                <SunTile
                  key={date}
                  date={date}
                  status={status}
                  today={isToday}
                  hasNote={Boolean(noteMap.get(date))}
                  clients={clients}
                  dimmed={clientId ? !matchesClient(date) : false}
                  onClick={() => setEditingDate(date)}
                />
              );
            })}
          </div>

          {/* No day bills to the filtered client this month. */}
          {clientId && !anyClientMatch && (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 24px" }}>
              <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, boxShadow: "0 6px 20px rgba(15,26,48,.14)", padding: "12px 15px", textAlign: "center", fontSize: 12.5, color: "var(--muted)", lineHeight: 1.45 }}>
                {t("clientFilter.noDays", { name: clientName })}
              </div>
            </div>
          )}
        </div>

        {/* Legend */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginTop: "14px",
            fontSize: "11px",
            color: "var(--muted2)",
          }}
        >
          <span
            style={{
              font: "600 10px var(--font-head)",
              letterSpacing: ".04em",
              textTransform: "uppercase",
            }}
          >
            Less
          </span>
          <span
            style={{
              width: "15px",
              height: "15px",
              borderRadius: "5px",
              background: "var(--tile-none)",
            }}
          />
          <span
            style={{
              width: "15px",
              height: "15px",
              borderRadius: "5px",
              background:
                "linear-gradient(to top, var(--helio-orange), transparent 78%), var(--tile-none)",
            }}
          />
          <span
            style={{
              width: "15px",
              height: "15px",
              borderRadius: "5px",
              background:
                "linear-gradient(to top, var(--helio-orange), transparent 45%), var(--tile-none)",
            }}
          />
          <span
            style={{
              width: "15px",
              height: "15px",
              borderRadius: "5px",
              background:
                "radial-gradient(circle at 50% 38%, var(--helio-orange-bright, #ff9a3c), var(--helio-orange) 72%)",
              boxShadow: "0 0 7px var(--glow)",
            }}
          />
          <span
            style={{
              font: "600 10px var(--font-head)",
              letterSpacing: ".04em",
              textTransform: "uppercase",
            }}
          >
            More
          </span>
        </div>
      </div>

      {/* ── Revenue — real per-period figures + set-aside, client-scoped ─── */}
      <div style={{ marginTop: "14px" }}>
        <RevenueCard month={month} clientId={clientId || undefined} clientName={clientName} />
      </div>

      {/* URSSAF — always-annual plafond & TVA (never client- or period-scoped).
          Under a filter it wears an "Activité globale" chip so its exemption
          reads as intentional, not stale. */}
      {urssafEnabled && (
        <div style={{ marginTop: "12px" }}>
          <CeilingCard filtered={Boolean(clientId)} />
        </div>
      )}

      {/* À venir — next fiscal/social deadlines (person-level, never filtered) */}
      <div style={{ marginTop: "12px" }}>
        <UpcomingCard />
      </div>

      {/* Log a day straight from Home — same editor as Calendar, no redirect. */}
      {editingDate && (
        <DayEditor
          date={editingDate}
          current={dayMap.get(editingDate) ?? null}
          month={month}
          currentNote={noteMap.get(editingDate) ?? ""}
          currentClientId={clientMap.get(editingDate) ?? null}
          currentAllocations={allocMap.get(editingDate) ?? []}
          onClose={() => setEditingDate(null)}
        />
      )}
    </div>
  );
}
